import { TranslateService } from '@ngx-translate/core';
import { MasterService } from '../services/master.service';
import { IOperationDocument, IOperationDocumentsDetail } from '../../processes/models/processes.models';
import { NOT_AVAILABLE_DISPLAY } from '../../core/utils/utils';

/** Fila para `tec-table` en la modal de concordancia documentos vs registro. */
export interface IDocumentsCompareTableRow {
	clickValue: string;
	notification: boolean;
	cells: { label: string }[];
}

/**
 * Construye filas de contraste nombre/apellidos (OCR del documento vs `names` / `surnames` del detalle).
 */
export function buildDocumentsCompareTableRows(
	doc: IOperationDocument | null,
	docsData: IOperationDocumentsDetail | null,
	trans: TranslateService,
): IDocumentsCompareTableRow[] {
	if (!doc) return [];
	const raw = doc?.data?.ocr as Record<string, unknown> | undefined;
	if (!raw) return [];
	const picked = pickNameAndSurnameOcrEntries(raw);
	const rows: IDocumentsCompareTableRow[] = [];
	let idx = 0;
	const pushRow = (group: 'name' | 'surname', el: Record<string, unknown>) => {
		const documentText = stringifyOcrText(el['text']);
		const compareDataText = resolveCompareDataValue(group, docsData);
		const notification = isRowMismatch(documentText, compareDataText);
		rows.push({
			clickValue: String(idx++),
			notification,
			cells: [
				{ label: compareRowTypeLabel(group, trans) },
				{ label: documentText || NOT_AVAILABLE_DISPLAY },
				{ label: compareDataText ?? NOT_AVAILABLE_DISPLAY },
			],
		});
	};
	if (picked.name) pushRow('name', picked.name.val);
	if (picked.surname) pushRow('surname', picked.surname.val);
	return rows;
}

/** Indica si algún documento tiene filas de contraste OCR vs registro. */
export function documentsCompareHasAnyTableRow(
	docs: IOperationDocument[] | undefined,
	docsData: IOperationDocumentsDetail | null,
	trans: TranslateService,
): boolean {
	if (!docs?.length) return false;
	return docs.some((d) => buildDocumentsCompareTableRows(d, docsData, trans).length > 0);
}

export function documentTypeLabelForCompare(
	doc: IOperationDocument,
	master: MasterService,
	trans: TranslateService,
): string {
	if (!doc?.type) return NOT_AVAILABLE_DISPLAY;
	const key = 'DOCS.' + doc.type;
	return master.hasTranslation(key) ? trans.instant(key) : doc.type;
}

function compareRowTypeLabel(group: 'name' | 'surname', trans: TranslateService): string {
	const key =
		group === 'name'
			? 'OPERATIONS.DETAIL.DOCUMENTS_COMPARE_ROW_NAME'
			: 'OPERATIONS.DETAIL.DOCUMENTS_COMPARE_ROW_SURNAME';
	return trans.instant(key);
}

function pickNameAndSurnameOcrEntries(raw: Record<string, unknown>): {
	name?: { val: Record<string, unknown> };
	surname?: { val: Record<string, unknown> };
} {
	const nameVal = raw['names'];
	const surnameVal = raw['surnames'];
	return {
		name: isOcrTextNode(nameVal) ? { val: nameVal } : undefined,
		surname: isOcrTextNode(surnameVal) ? { val: surnameVal } : undefined,
	};
}

function isOcrTextNode(val: unknown): val is Record<string, unknown> {
	return typeof val === 'object' && val !== null && 'text' in val;
}

function stringifyOcrText(text: unknown): string {
	if (typeof text === 'string' || typeof text === 'number' || typeof text === 'boolean') {
		return String(text);
	}
	return JSON.stringify(text ?? '');
}

function resolveCompareDataValue(
	group: 'name' | 'surname',
	docsData: IOperationDocumentsDetail | null,
): string | undefined {
	const rootValue = group === 'name' ? docsData?.names : docsData?.surnames;
	if (rootValue === undefined || rootValue === null || rootValue === '') {
		return undefined;
	}
	return String(rootValue);
}

function isRowMismatch(documentText: string, registryText: string | undefined): boolean {
	if (registryText === undefined || registryText === '') {
		return false;
	}
	return normalizeCompare(documentText) !== normalizeCompare(registryText);
}

function normalizeCompare(value: string): string {
	return value.trim().toLowerCase().replace(/\s+/g, ' ');
}
